import { FC, useEffect, useState, useRef } from 'react';
/* eslint-disable react/jsx-props-no-spreading */
import { DODA_PHOTO_DISPLAY_API_DOMAIN } from '@doda/common/src/constants/urls';
import styles from './index.module.scss';
import { SectionTitle } from '../SectionTitle';

/**
 * 写真タブボディコンテンツのProps
 */
export interface PhotoProps {
  publicationPhotos: string[];
  latestUpdateDate: string;
}

/**
 * 写真タブボディコンテンツ
 */
export const PublicationPhotoContent: FC<PhotoProps> = ({
  publicationPhotos,
  latestUpdateDate,
}) => {
  // 日付をyyyy年M月にフォーマット
  const formattedDate = latestUpdateDate
    ? formatDateToYearMonth(latestUpdateDate)
    : '';

  return (
    <section className={styles.imageGallery__area}>
      <SectionTitle mb="L" border>
        この企業の写真
      </SectionTitle>
      <PublicationPhotoGallery photoUrls={publicationPhotos} />
      <p className={styles.imageGallery__updateDate}>{formattedDate}更新</p>
      <div className={styles.imageGallery__photosDescription__area}>
        <p className={styles.imageGallery__photosDescription}>
          写真はこれまでdodaの求人で撮影されたものです。募集職種や勤務先が異なる写真が含まれている可能性があります。
        </p>
        <p className={styles.imageGallery__photosDescription}>
          掲載写真に関するご相談ご要望は、求人企業へ直接ご連絡ください。dodaでの修正・削除などはお受けいたしかねますので、予めご了承ください。
        </p>
      </div>
    </section>
  );
};

/**
 * yyyy年M月に変換
 */
const formatDateToYearMonth = (dateString) => {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = date.getMonth() + 1; // 月は0から始まるため、1を足します
  return `${year}年${month}月`;
};

/**
 * 画像表示URLを返す.
 *
 * @param imagePath 画像表示APIのパス
 * @returns 画像表示URL
 */
const createImagePath = (imagePath: string) => {
  const env = process.env.APP_ENV as string;
  let domain = DODA_PHOTO_DISPLAY_API_DOMAIN[env];
  if (!domain) {
    const { host } = window.location;
    // ホスト名とドメインのマッピング
    const hostToDomainMap: { [key: string]: string } = {
      'doda.jp': 'https://src.doda.jp',
      'test-staging.doda.jp': 'https://test-src.doda.jp',
      'test-st.doda.jp': 'https://test-st.doda.jp',
    };
    // それ以外の場合はデフォルトの処理としてホスト名を使用
    domain = hostToDomainMap[host] || `https://${host}`;
  }
  return `${domain}${imagePath}`;
};

/**
 * 写真一覧用URLの型
 */
interface PublicationPhotoGalleryProps {
  photoUrls: string[];
}

const PublicationPhotoGallery: FC<PublicationPhotoGalleryProps> = ({
  photoUrls,
}) => {
  const [loadedImages, setLoadedImages] = useState<number[]>([]);
  const itemRefs = useRef<(HTMLImageElement | null)[]>([]);

  useEffect(() => {
    itemRefs.current.forEach((item, idx) => {
      if (item) {
        item.decode().then(() => {
          setLoadedImages((prevState) => [...prevState, idx]);
        });
      }
    });
  }, []);

  return (
    <div className={styles.imageGallery}>
      {photoUrls.map((item, idx) => (
        <div
          className={`${styles.imageGallery__imageWrap} ${!loadedImages.includes(idx) ? styles.unloaded : ''}`.trim()}
          key={idx}
        >
          <img
            src={createImagePath(item)}
            alt=""
            className={styles.imageGallery__image}
            width="293"
            height="293"
            loading="lazy"
            ref={(el) => {
              itemRefs.current[idx] = el;
            }}
          />
        </div>
      ))}
    </div>
  );
};
